import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addBundleDefaultComponent,
  copyBundleDefaultsFromPim,
  createBundleDefaultList,
  deleteBundleDefaultList,
  fetchBundleDefaults,
  removeBundleDefaultComponent,
  updateBundleDefaultComponentQuantity,
} from '../../services/bundleDefaultsAPI';
import { getAllSKUs, getBundleParentSkus } from '../../services/SKUListAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import type { BundleDefaultListEntry } from '../../schemas/apiResponse.schemas';
import { SKU_PREFIX } from '../../constants/app.constants';
import { normalizeSku } from '../../utils/normalizeSku';
import { useSkuAutocomplete } from '../../hooks/useSkuAutocomplete';
import SkuAutocompleteInput from '../shared/SkuAutocompleteInput';
import BundleAllowlistCard from './BundleAllowlistCard';
import {
  excludeAllowlistedSkus,
  orderAllowlistEntries,
  pinAllowlistSku,
  unpinAllowlistSku,
} from './bundleAllowlistOrder';
import '../labels/PrintBoxLabel.css';
import './BundleScanSubsetManage.css';

const BundleDefaultsManage: React.FC = () => {
  const [entries, setEntries] = useState<BundleDefaultListEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [parentSkus, setParentSkus] = useState<string[]>([]);
  const [allSkus, setAllSkus] = useState<string[]>([]);
  const [parentSkusLoading, setParentSkusLoading] = useState<boolean>(true);
  const [parentSkuInput, setParentSkuInput] = useState<string>(SKU_PREFIX);
  const [acting, setActing] = useState<boolean>(false);
  const [removingSku, setRemovingSku] = useState<string | null>(null);
  const [sessionAddedSkus, setSessionAddedSkus] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [componentInputs, setComponentInputs] = useState<Record<string, string>>({});
  const [componentActingSku, setComponentActingSku] = useState<string | null>(null);

  const skuInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const skipAutocompleteOpenRef = useRef(false);

  const searchableParentSkus = useMemo(
    () =>
      excludeAllowlistedSkus(
        parentSkus,
        entries.map((entry) => entry.parentSku)
      ),
    [parentSkus, entries]
  );

  const {
    filteredSkus,
    showAutocomplete,
    setShowAutocomplete,
    selectedIndex,
    setSelectedIndex,
  } = useSkuAutocomplete({
    value: parentSkuInput,
    allSkus: searchableParentSkus,
    prefix: SKU_PREFIX,
    skipAutocompleteOpenRef,
    inputRef: skuInputRef,
    autocompleteRef,
  });

  const parentSkuSet = useMemo(
    () => new Set(parentSkus.map((sku) => normalizeSku(sku))),
    [parentSkus]
  );

  const loadEntries = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchBundleDefaults();
      setSessionAddedSkus([]);
      setEntries(list);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to load bundle default lists'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    let cancelled = false;
    const loadCatalog = async (): Promise<void> => {
      setParentSkusLoading(true);
      try {
        const [parents, skus] = await Promise.all([getBundleParentSkus(), getAllSKUs()]);
        if (!cancelled) {
          setParentSkus(parents);
          setAllSkus(skus);
        }
      } catch (err: unknown) {
        console.error('Failed to load SKU catalogs:', err);
        if (!cancelled) {
          setParentSkus([]);
          setAllSkus([]);
        }
      } finally {
        if (!cancelled) {
          setParentSkusLoading(false);
        }
      }
    };
    void loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  const isAcceptedParentSku = useCallback(
    (sku: string): boolean => parentSkuSet.has(normalizeSku(sku)),
    [parentSkuSet]
  );

  const handleSkuInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    if (value.length < SKU_PREFIX.length) {
      setParentSkuInput(SKU_PREFIX);
    } else if (!value.startsWith(SKU_PREFIX)) {
      setParentSkuInput(SKU_PREFIX + value.substring(SKU_PREFIX.length));
    } else {
      setParentSkuInput(value.toUpperCase());
    }
  };

  const selectSku = (sku: string): void => {
    if (!isAcceptedParentSku(sku)) {
      setError(`SKU ${sku} is not a bundle parent`);
      setMessage(null);
      return;
    }
    skipAutocompleteOpenRef.current = true;
    setParentSkuInput(sku);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    setError(null);
  };

  const handleSkuInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!showAutocomplete || filteredSkus.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        void handleAdd();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < filteredSkus.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev + 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      selectSku(filteredSkus[selectedIndex]);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      void handleAdd();
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const replaceEntry = (updated: BundleDefaultListEntry): void => {
    setEntries((prev) => prev.map((entry) => (entry.parentSku === updated.parentSku ? updated : entry)));
  };

  const handleAdd = async (): Promise<void> => {
    const sku = parentSkuInput.trim();
    if (!sku || sku.length <= SKU_PREFIX.length || acting) return;

    if (!isAcceptedParentSku(sku)) {
      setError('Only SKUs listed as bundle parents can be added');
      setMessage(null);
      return;
    }

    setActing(true);
    setError(null);
    setMessage(null);
    try {
      const entry = await createBundleDefaultList(sku);
      setEntries((prev) => [...prev, entry]);
      setSessionAddedSkus((prev) => pinAllowlistSku(prev, entry.parentSku));
      skipAutocompleteOpenRef.current = true;
      setParentSkuInput(SKU_PREFIX);
      setShowAutocomplete(false);
      setMessage(`Added ${entry.parentSku}`);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to add parent SKU'));
    } finally {
      setActing(false);
    }
  };

  const handleRemove = async (entry: BundleDefaultListEntry): Promise<void> => {
    const confirmed = window.confirm(
      `Delete the default component list for "${entry.parentSku}"?`
    );
    if (!confirmed) return;

    setRemovingSku(entry.parentSku);
    setError(null);
    setMessage(null);
    try {
      await deleteBundleDefaultList(entry.parentSku);
      setEntries((prev) => prev.filter((e) => e.parentSku !== entry.parentSku));
      setSessionAddedSkus((prev) => unpinAllowlistSku(prev, entry.parentSku));
      setMessage(`Removed ${entry.parentSku}`);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to remove default list'));
    } finally {
      setRemovingSku(null);
    }
  };

  const handleCopyFromPim = async (entry: BundleDefaultListEntry): Promise<void> => {
    setComponentActingSku(entry.parentSku);
    setError(null);
    setMessage(null);
    try {
      const updated = await copyBundleDefaultsFromPim(entry.parentSku);
      replaceEntry(updated);
      setMessage(`Copied PIM BOM onto ${entry.parentSku}`);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to copy PIM BOM'));
    } finally {
      setComponentActingSku(null);
    }
  };

  const handleAddComponent = async (entry: BundleDefaultListEntry): Promise<void> => {
    const sku = (componentInputs[entry.parentSku] ?? SKU_PREFIX).trim();
    if (!sku || sku.length <= SKU_PREFIX.length || componentActingSku) return;

    setComponentActingSku(entry.parentSku);
    setError(null);
    setMessage(null);
    try {
      const component = await addBundleDefaultComponent(entry.parentSku, sku, 1);
      replaceEntry({
        ...entry,
        components: [...entry.components, component],
      });
      setComponentInputs((prev) => ({ ...prev, [entry.parentSku]: SKU_PREFIX }));
      setMessage(`Added ${component.sku} to ${entry.parentSku}`);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to add component'));
    } finally {
      setComponentActingSku(null);
    }
  };

  const handleQuantityChange = async (
    entry: BundleDefaultListEntry,
    componentSku: string,
    quantity: number,
  ): Promise<void> => {
    const component = entry.components.find((row) => row.sku === componentSku);
    if (!component) return;
    setComponentActingSku(entry.parentSku);
    setError(null);
    try {
      const updated = await updateBundleDefaultComponentQuantity(entry.parentSku, component.id, quantity);
      replaceEntry({
        ...entry,
        components: entry.components.map((row) => (row.id === updated.id ? updated : row)),
      });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update quantity'));
    } finally {
      setComponentActingSku(null);
    }
  };

  const handleRemoveComponent = async (entry: BundleDefaultListEntry, componentSku: string): Promise<void> => {
    const component = entry.components.find((row) => row.sku === componentSku);
    if (!component) return;
    setComponentActingSku(entry.parentSku);
    setError(null);
    try {
      await removeBundleDefaultComponent(entry.parentSku, component.id);
      replaceEntry({
        ...entry,
        components: entry.components.filter((row) => row.id !== component.id),
      });
      setMessage(`Removed ${componentSku} from ${entry.parentSku}`);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to remove component'));
    } finally {
      setComponentActingSku(null);
    }
  };

  const displayedEntries = useMemo(
    () =>
      orderAllowlistEntries({
        entries,
        getSku: (entry) => entry.parentSku,
        pinnedSkus: sessionAddedSkus,
      }),
    [entries, sessionAddedSkus]
  );

  const canAdd =
    !acting &&
    !parentSkusLoading &&
    parentSkuInput.trim().length > SKU_PREFIX.length &&
    isAcceptedParentSku(parentSkuInput);

  return (
    <div className="pbl-container bssm-container">
      <p className="bssm-help">
        Default component lists are the scan lines added when a bundle parent arrives
        on a ShipStation order with no components (quick add). Include the parent SKU
        on the list if it is a physical item that must be scanned. Copy from PIM is a
        starting point only — fill never falls back to the catalog BOM.
      </p>

      <div className="pbl-section">
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">Add Parent Bundle SKU</span>
        </div>
        <div className="bssm-add-row">
          <div className="bssm-sku-finder">
            <SkuAutocompleteInput
              inputRef={skuInputRef}
              autocompleteRef={autocompleteRef}
              value={parentSkuInput}
              onChange={handleSkuInputChange}
              onKeyDown={handleSkuInputKeyDown}
              placeholder={`${SKU_PREFIX}...`}
              showAutocomplete={showAutocomplete}
              filteredSkus={filteredSkus}
              selectedIndex={selectedIndex}
              onSelectSku={selectSku}
              onHoverIndex={setSelectedIndex}
            />
          </div>
          <button
            type="button"
            className="bssm-add-btn"
            onClick={() => void handleAdd()}
            disabled={!canAdd}
          >
            {acting ? 'Adding...' : 'Add'}
          </button>
        </div>
        {parentSkusLoading && (
          <div className="pbl-message">Loading bundle parent SKUs...</div>
        )}
      </div>

      {error && <div className="bssm-banner bssm-banner--error">{error}</div>}
      {message && <div className="bssm-banner bssm-banner--ok">{message}</div>}

      <div className="pbl-section">
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">
            Default lists{loading ? '' : ` (${entries.length})`}
          </span>
        </div>
        {loading ? (
          <div className="pbl-message">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="pbl-message">No default component lists configured.</div>
        ) : (
          <ul className="bssm-list">
            {displayedEntries.map((entry) => (
              <BundleDefaultListCard
                key={entry.parentSku}
                entry={entry}
                allSkus={allSkus}
                componentInput={componentInputs[entry.parentSku] ?? SKU_PREFIX}
                onComponentInputChange={(value) => {
                  setComponentInputs((prev) => ({ ...prev, [entry.parentSku]: value }));
                }}
                acting={componentActingSku === entry.parentSku}
                removeDisabled={removingSku === entry.parentSku}
                onRemove={() => void handleRemove(entry)}
                onCopyFromPim={() => void handleCopyFromPim(entry)}
                onAddComponent={() => void handleAddComponent(entry)}
                onQuantityChange={(sku, quantity) => void handleQuantityChange(entry, sku, quantity)}
                onRemoveComponent={(sku) => void handleRemoveComponent(entry, sku)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

interface BundleDefaultListCardProps {
  readonly entry: BundleDefaultListEntry;
  readonly allSkus: readonly string[];
  readonly componentInput: string;
  readonly onComponentInputChange: (value: string) => void;
  readonly acting: boolean;
  readonly removeDisabled: boolean;
  readonly onRemove: () => void;
  readonly onCopyFromPim: () => void;
  readonly onAddComponent: () => void;
  readonly onQuantityChange: (sku: string, quantity: number) => void;
  readonly onRemoveComponent: (sku: string) => void;
}

function BundleDefaultListCard({
  entry,
  allSkus,
  componentInput,
  onComponentInputChange,
  acting,
  removeDisabled,
  onRemove,
  onCopyFromPim,
  onAddComponent,
  onQuantityChange,
  onRemoveComponent,
}: BundleDefaultListCardProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const skipAutocompleteOpenRef = useRef(false);
  const listedSkus = useMemo(
    () => new Set(entry.components.map((component) => normalizeSku(component.sku))),
    [entry.components]
  );
  const searchable = useMemo(
    () => allSkus.filter((sku) => !listedSkus.has(normalizeSku(sku))),
    [allSkus, listedSkus]
  );

  const {
    filteredSkus,
    showAutocomplete,
    setShowAutocomplete,
    selectedIndex,
    setSelectedIndex,
  } = useSkuAutocomplete({
    value: componentInput,
    allSkus: searchable,
    prefix: SKU_PREFIX,
    skipAutocompleteOpenRef,
    inputRef,
    autocompleteRef,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    if (value.length < SKU_PREFIX.length) {
      onComponentInputChange(SKU_PREFIX);
    } else if (!value.startsWith(SKU_PREFIX)) {
      onComponentInputChange(SKU_PREFIX + value.substring(SKU_PREFIX.length));
    } else {
      onComponentInputChange(value.toUpperCase());
    }
  };

  return (
    <BundleAllowlistCard
      parentSku={entry.parentSku}
      components={entry.components}
      removeDisabled={removeDisabled}
      removeLabel={removeDisabled ? 'Removing...' : 'Remove'}
      onRemove={onRemove}
      emptyComponentsLabel="No default components yet"
      onRemoveComponent={onRemoveComponent}
      onQuantityChange={onQuantityChange}
      componentRemoveDisabled={acting}
      expandedFooter={(
        <>
          <div className="bssm-sku-finder">
            <SkuAutocompleteInput
              inputRef={inputRef}
              autocompleteRef={autocompleteRef}
              value={componentInput}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (selectedIndex >= 0 && filteredSkus[selectedIndex]) {
                    onComponentInputChange(filteredSkus[selectedIndex]);
                    setShowAutocomplete(false);
                    return;
                  }
                  onAddComponent();
                }
              }}
              placeholder={`${SKU_PREFIX}...`}
              showAutocomplete={showAutocomplete}
              filteredSkus={filteredSkus}
              selectedIndex={selectedIndex}
              onSelectSku={(sku) => {
                skipAutocompleteOpenRef.current = true;
                onComponentInputChange(sku);
                setShowAutocomplete(false);
                setSelectedIndex(-1);
              }}
              onHoverIndex={setSelectedIndex}
            />
          </div>
          <button
            type="button"
            className="bssm-add-btn"
            disabled={acting || componentInput.trim().length <= SKU_PREFIX.length}
            onClick={onAddComponent}
          >
            {acting ? 'Saving...' : 'Add component'}
          </button>
          <button
            type="button"
            className="bssm-remove-btn"
            disabled={acting}
            onClick={onCopyFromPim}
          >
            Copy from PIM
          </button>
        </>
      )}
    />
  );
}

export default BundleDefaultsManage;
